import { WorkbenchLink } from '@affine/core/modules/workbench';
import { ToggleDownIcon } from '@blocksuite/icons/rc';
import * as Collapsible from '@radix-ui/react-collapsible';
import { useCallback, useMemo, useState } from 'react';

import type { NotesEntryStatus } from '../handlers/add-to-notes';
import { useNotesIndex } from '../hooks/use-notes-index';
// Reuse the branches panel's simple visual language so both inline
// sections at the bottom of a doc read as part of the same family.
import * as styles from './branches-inline-panel.css';

interface NotesRowSummary {
  entryId: string;
  passage: string;
  createdAt: string;
  status: NotesEntryStatus;
  tags: string[];
}

/**
 * Inline "Saved to Notes" section that renders at the bottom of the
 * doc body, listing every clipping from THIS doc that the user has
 * sent to their Research Notes. Click a row → opens the Notes doc
 * and scrolls to the entry's divider (via AffineOS's standard
 * `?blockIds=` plumbing routed through `CanvasLinkRouter`).
 *
 * Hides itself entirely when this doc has no entries — keeps the
 * doc body uncluttered for papers the user hasn't clipped yet.
 */
export function NotesInlinePanel({ docId }: { docId: string }) {
  const notesIndex = useNotesIndex();
  // Default-expanded: notes lists are usually short and the user
  // explicitly clipped each entry, so hiding by default would make
  // their own work invisible. The collapse is still here for
  // clutter control on heavy reading days.
  const [open, setOpen] = useState(true);

  const rows = useMemo<NotesRowSummary[]>(() => {
    const entries = notesIndex.getEntries(docId);
    if (entries.length === 0) return [];
    return entries.map(entry => ({
      entryId: entry.entryId,
      passage: entry.sourcePassage.replace(/\s+/g, ' ').trim(),
      createdAt: entry.createdAt,
      status: entry.status ?? 'inbox',
      tags: entry.tags ?? [],
    }));
  }, [notesIndex, docId]);

  const onToggle = useCallback(() => setOpen(v => !v), []);

  // Hide entirely when there's nothing from this doc — also bails
  // when no Notes doc exists yet at the workspace level.
  if (!notesIndex.notesDocId || rows.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.divider} />
      <Collapsible.Root open={open} onOpenChange={onToggle}>
        <div className={styles.headerRow}>
          <Collapsible.Trigger className={styles.header} type="button">
            <span className={styles.headerLabel}>Saved to Notes</span>
            <span className={styles.headerCount}>· {rows.length}</span>
            <ToggleDownIcon
              className={styles.headerCaret}
              data-open={open ? 'true' : 'false'}
            />
          </Collapsible.Trigger>
          {/* Browse-all link — anchors the user to the workspace-wide
              review surface so they don't fall into the "endless
              backlog" trap of only ever seeing this-doc's clippings. */}
          <WorkbenchLink
            to="/notes"
            draggable={false}
            className={styles.headerBrowseLink}
            title="Open the Notes browser to see all your clippings"
          >
            Open Notes browser →
          </WorkbenchLink>
        </div>
        <Collapsible.Content>
          <ol className={styles.list}>
            {rows.map(row => (
              <li key={row.entryId}>
                <WorkbenchLink
                  to={{
                    pathname: `/${notesIndex.notesDocId}`,
                    search: `?mode=page&blockIds=${encodeURIComponent(row.entryId)}`,
                    hash: '',
                  }}
                  draggable={false}
                  className={styles.row}
                >
                  <span className={styles.rowBody}>
                    <span
                      className={styles.statusDot}
                      data-status={row.status}
                      title={`Status: ${row.status}`}
                      aria-hidden
                    />
                    <span className={styles.rowTitle}>
                      {truncatePassage(row.passage)}
                    </span>
                    {row.tags.map(tag => (
                      <span key={tag} className={styles.inlineTag}>
                        {tag}
                      </span>
                    ))}
                  </span>
                  {row.createdAt ? (
                    <span className={styles.rowDate}>
                      {formatRelativeDate(row.createdAt)}
                    </span>
                  ) : null}
                </WorkbenchLink>
              </li>
            ))}
          </ol>
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
}

function truncatePassage(passage: string): string {
  if (passage.length <= 90) return passage;
  return `${passage.slice(0, 90).trimEnd()}\u2026`;
}

function formatRelativeDate(iso: string): string {
  if (!iso) return '';
  try {
    const date = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.round(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year:
        date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  } catch {
    return '';
  }
}
